import React, { useState, useEffect } from 'react';
import ResearchLayout from '../../components/ResearchLayout';
import { useAuth } from '../../contexts/AuthContext';
import api from '../../services/api';

const ProgramAdminAddDoctors = () => {
  const { user } = useAuth();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: '',
    confirmPassword: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [successMessage, setSuccessMessage] = useState('Doctor account created successfully.');
  const [doctors, setDoctors] = useState([]);
  
  // State for update and delete operations
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteDoctor, setDeleteDoctor] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [deactivateOnly, setDeactivateOnly] = useState(false);
  
  // State for reactivate modal
  const [showReactivateModal, setShowReactivateModal] = useState(false);
  const [reactivateDoctor, setReactivateDoctor] = useState(null);
  const [isReactivating, setIsReactivating] = useState(false);

  const [showEditModal, setShowEditModal] = useState(false);
  const [editDoctor, setEditDoctor] = useState(null);
  const [editFormData, setEditFormData] = useState({
    name: '',
    email: '',
    password: '',
    confirmPassword: ''
  });
  const [isUpdating, setIsUpdating] = useState(false);
  const [error, setError] = useState('');

  // State for password change modal
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [passwordDoctor, setPasswordDoctor] = useState(null);
  const [passwordFormData, setPasswordFormData] = useState({
    newPassword: '',
    confirmNewPassword: ''
  });
  const [isChangingPassword, setIsChangingPassword] = useState(false);

  // State to control visibility of inactive doctors
  const [showInactiveDoctors, setShowInactiveDoctors] = useState(false);

  // Fetch doctors from backend
  const loadDoctors = async () => {
    try {
      // Include inactive doctors in the request if needed
      const response = await api.get(`/doctors${showInactiveDoctors ? '?include_inactive=true' : ''}`);
      console.log('Doctors API response:', response.data);
      
      if (response.data.success && response.data.doctors) {
        setDoctors(response.data.doctors);
      } else {
        setDoctors([]);
      }
    } catch (err) {
      console.error('Error loading doctors:', err);
      setDoctors([]);
      setError('Failed to load doctors. Please refresh the page.');
    }
  };

  useEffect(() => {
    loadDoctors();
  }, [showInactiveDoctors]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleEditInputChange = (e) => {
    const { name, value } = e.target;
    setEditFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setError('');

    try {
      // Validation
      if (formData.password !== formData.confirmPassword) {
        setError('Passwords do not match.');
        setIsSubmitting(false);
        return;
      }

      if (formData.password.length < 6) {
        setError('Password must be at least 6 characters long.');
        setIsSubmitting(false);
        return;
      }

      // Check if email already exists in backend
      if (doctors.some(u => u.email.toLowerCase() === formData.email.toLowerCase())) {
        setError('A user with this email already exists.');
        setIsSubmitting(false);
        return;
      }

      // Create new doctor via backend
      await api.post('/doctors', {
        name: formData.name,
        email: formData.email,
        password: formData.password
      });

      // Show success notification
      setSuccessMessage('Doctor account created successfully.');
      setShowSuccess(true);
      setFormData({
        name: '',
        email: '',
        password: '',
        confirmPassword: ''
      });

      // Reload doctors from backend
      await loadDoctors();

      // Hide success message after 3 seconds
      setTimeout(() => setShowSuccess(false), 3000);

    } catch (error) {
      console.error('Error creating doctor:', error);
      setError('Failed to create doctor account. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };
  
  // Handle initiating the edit process
  const handleEditClick = (doctor) => {
    setEditDoctor(doctor);
    setEditFormData({
      name: doctor.name,
      email: doctor.email,
      password: '',
      confirmPassword: ''
    });
    setShowEditModal(true);
  };
  
  // Handle submitting the edit form
  const handleEditSubmit = async (e) => {
    e.preventDefault();
    setIsUpdating(true);
    setError('');
    
    try {
      // Validate if there's a password change
      if (editFormData.password) {
        if (editFormData.password !== editFormData.confirmPassword) {
          setError('Passwords do not match.');
          setIsUpdating(false);
          return;
        }
        
        if (editFormData.password.length < 6) {
          setError('Password must be at least 6 characters long.');
          setIsUpdating(false);
          return;
        }
      }
      
      // Check if email already exists (excluding the current doctor)
      if (doctors.some(d => d.email.toLowerCase() === editFormData.email.toLowerCase() && d.id !== editDoctor.id)) {
        setError('A user with this email already exists.');
        setIsUpdating(false);
        return;
      }
      
      // Prepare update data
      const updateData = {
        name: editFormData.name,
        email: editFormData.email
      };
      
      // Include password only if it was changed
      if (editFormData.password) {
        updateData.password = editFormData.password;
      }
      
      // Send update request
      await api.put(`/doctors/${editDoctor.id}`, updateData);
      
      // Show success and reset
      setSuccessMessage('Doctor account updated successfully.');
      setShowSuccess(true);
      setShowEditModal(false);
      
      // Reload doctors
      await loadDoctors();
      
      // Hide success message after 3 seconds
      setTimeout(() => setShowSuccess(false), 3000);
      
    } catch (error) {
      console.error('Error updating doctor:', error);
      setError('Failed to update doctor account. Please try again.');
    } finally {
      setIsUpdating(false);
    }
  };
  
  // Handle initiating delete
  const handleDeleteClick = (doctor) => {
    setDeleteDoctor(doctor);
    setDeactivateOnly(false); // Reset the option
    setShowDeleteModal(true);
  };
  
  // Handle confirming delete
  const handleDeleteConfirm = async () => {
    setIsDeleting(true);
    try {
      let response;
      
      if (deactivateOnly) {
        // Use deactivate endpoint - just set is_active to false
        response = await api.put(`/doctors/${deleteDoctor.id}/deactivate`);
      } else {
        // Use delete endpoint - soft delete with email prefix
        response = await api.delete(`/doctors/${deleteDoctor.id}`);
      }
      
      // Check if it was a soft delete
      const wasSoftDeleted = response.data.soft_delete === true || deactivateOnly;
      
      // Remove the deleted doctor from the local state immediately
      setDoctors(currentDoctors => 
        currentDoctors.filter(doctor => doctor.id !== deleteDoctor.id)
      );
      
      // Show success with appropriate message
      setSuccessMessage(deactivateOnly 
        ? 'Doctor deactivated successfully.'
        : wasSoftDeleted 
          ? 'Doctor account deactivated successfully.' 
          : 'Doctor account deleted successfully.'
      );
      setShowSuccess(true);
      setShowDeleteModal(false);
      
      // Hide success message after 3 seconds
      setTimeout(() => setShowSuccess(false), 3000);
      
    } catch (error) {
      console.error('Error deleting doctor:', error);
      setError('Failed to delete doctor account. Please try again.');
    } finally {
      setIsDeleting(false);
    }
  };

  // Handle reactivate doctor - open modal
  const handleReactivateClick = (doctor) => {
    setReactivateDoctor(doctor);
    setShowReactivateModal(true);
  };

  // Handle confirming reactivate
  const handleReactivateConfirm = async () => {
    setIsReactivating(true);
    try {
      const response = await api.put(`/doctors/${reactivateDoctor.id}/reactivate`);
      
      // Update the doctor's is_active in local state
      setDoctors(currentDoctors => 
        currentDoctors.map(d => 
          d.id === reactivateDoctor.id ? { ...d, is_active: true } : d
        )
      );
      
      setSuccessMessage(response.data.message);
      setShowSuccess(true);
      setShowReactivateModal(false);
      
      // Hide success message after 3 seconds
      setTimeout(() => setShowSuccess(false), 3000);
      
    } catch (error) {
      console.error('Error reactivating doctor:', error);
      setError('Failed to reactivate doctor account. Please try again.');
    } finally {
      setIsReactivating(false);
    }
  };

  const generatePassword = () => {
    const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789!@#$%^&*';
    let password = '';
    for (let i = 0; i < 12; i++) {
      password += chars.charAt(Math.floor(Math.random() * chars.length));
    }
    setFormData(prev => ({
      ...prev,
      password: password,
      confirmPassword: password
    }));
  };
  
  const generateEditPassword = () => {
    const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789!@#$%^&*';
    let password = '';
    for (let i = 0; i < 12; i++) {
      password += chars.charAt(Math.floor(Math.random() * chars.length));
    }
    setEditFormData(prev => ({
      ...prev,
      password: password,
      confirmPassword: password
    }));
  };

  // Password change handlers
  const handlePasswordInputChange = (e) => {
    const { name, value } = e.target;
    setPasswordFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handlePasswordClick = (doctor) => {
    setPasswordDoctor(doctor);
    setPasswordFormData({
      newPassword: '',
      confirmNewPassword: ''
    });
    setShowPasswordModal(true);
  };

  const handlePasswordSubmit = async (e) => {
    e.preventDefault();
    setIsChangingPassword(true);
    setError('');

    try {
      // Validation
      if (passwordFormData.newPassword !== passwordFormData.confirmNewPassword) {
        setError('Passwords do not match.');
        setIsChangingPassword(false);
        return;
      }

      if (passwordFormData.newPassword.length < 6) {
        setError('Password must be at least 6 characters long.');
        setIsChangingPassword(false);
        return;
      }

      // Send password change request to the dedicated endpoint
      await api.put(`/doctors/${passwordDoctor.id}/change-password`, {
        new_password: passwordFormData.newPassword
      });

      // Show success and reset
      setSuccessMessage(`Password for Dr. ${passwordDoctor.name} has been updated successfully.`);
      setShowSuccess(true);
      setShowPasswordModal(false);

      // Hide success message after 3 seconds
      setTimeout(() => setShowSuccess(false), 3000);

    } catch (error) {
      console.error('Error changing doctor password:', error);
      setError('Failed to update doctor password. Please try again.');
    } finally {
      setIsChangingPassword(false);
    }
  };

  const generatePasswordChangePassword = () => {
    const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789!@#$%^&*';
    let password = '';
    for (let i = 0; i < 12; i++) {
      password += chars.charAt(Math.floor(Math.random() * chars.length));
    }
    setPasswordFormData(prev => ({
      ...prev,
      newPassword: password,
      confirmNewPassword: password
    }));
  };

  return (
    <ResearchLayout pageTitle="Add New Doctors">
      <div className="container-fluid">
        {/* Success Alert */}
        {showSuccess && (
          <div className="alert alert-success alert-dismissible fade show mb-4" role="alert">
            <i className="fas fa-check-circle me-2"></i>
            <strong>Success!</strong> {successMessage}
            <button type="button" className="btn-close" onClick={() => setShowSuccess(false)}></button>
          </div>
        )}
        
        {/* Error Alert */}
        {error && (
          <div className="alert alert-danger alert-dismissible fade show mb-4" role="alert">
            <i className="fas fa-exclamation-triangle me-2"></i>
            <strong>Error:</strong> {error}
            <button type="button" className="btn-close" onClick={() => setError('')}></button>
          </div>
        )}

        <div className="row">
          {/* Add Doctor Form */}
          <div className="col-lg-8">
            <div className="card border-0 shadow-sm">
              <div className="card-header bg-success text-white">
                <h5 className="mb-0">
                  <i className="fas fa-user-plus me-2"></i>
                  Add New Doctor
                </h5>
              </div>
              <div className="card-body p-4">
                <form onSubmit={handleSubmit}>
                  <div className="row mb-4">
                    <div className="col-md-6">
                      <label htmlFor="name" className="form-label fw-bold">
                        <i className="fas fa-user me-2 text-success"></i>
                        Full Name *
                      </label>
                      <input
                        type="text"
                        className="form-control form-control-lg"
                        id="name"
                        name="name"
                        value={formData.name}
                        onChange={handleInputChange}
                        placeholder="Dr. John Smith"
                        required
                      />
                    </div>
                    <div className="col-md-6">
                      <label htmlFor="email" className="form-label fw-bold">
                        <i className="fas fa-envelope me-2 text-success"></i>
                        Email Address *
                      </label>
                      <input
                        type="email"
                        className="form-control form-control-lg"
                        id="email"
                        name="email"
                        value={formData.email}
                        onChange={handleInputChange}
                        placeholder="john.smith@hospital.com"
                        required
                      />
                    </div>
                  </div>

                  <div className="row mb-4">
                    <div className="col-md-6">
                      <label htmlFor="password" className="form-label fw-bold">
                        <i className="fas fa-lock me-2 text-success"></i>
                        Password *
                      </label>
                      <div className="input-group">
                        <input
                          type="password"
                          className="form-control form-control-lg"
                          id="password"
                          name="password"
                          value={formData.password}
                          onChange={handleInputChange}
                          placeholder="Enter secure password"
                          required
                        />
                        <button
                          type="button"
                          className="btn btn-outline-success"
                          onClick={generatePassword}
                          title="Generate secure password"
                        >
                          <i className="fas fa-random"></i>
                        </button>
                      </div>
                      <div className="form-text">
                        Password must be at least 6 characters long.
                      </div>
                    </div>
                    <div className="col-md-6">
                      <label htmlFor="confirmPassword" className="form-label fw-bold">
                        <i className="fas fa-check me-2 text-success"></i>
                        Confirm Password *
                      </label>
                      <input
                        type="password"
                        className="form-control form-control-lg"
                        id="confirmPassword"
                        name="confirmPassword"
                        value={formData.confirmPassword}
                        onChange={handleInputChange}
                        placeholder="Confirm password"
                        required
                      />
                    </div>
                  </div>

                  <div className="bg-light p-3 rounded mb-4">
                    <h6 className="text-success mb-3">
                      <i className="fas fa-info-circle me-2"></i>
                      Doctor Account Guidelines
                    </h6>
                    <ul className="mb-0 small">
                      <li>Doctor will have access to assigned protocols and cases</li>
                      <li>They can answer research questions for their assigned cases</li>
                      <li>Doctors cannot edit answers once submitted</li>
                      <li>Email will be used for login credentials</li>
                      <li>Ensure the email address is valid and accessible</li>
                    </ul>
                  </div>

                  <div className="d-flex justify-content-between align-items-center">
                    <button
                      type="button"
                      className="btn btn-outline-secondary"
                      onClick={() => setFormData({
                        name: '',
                        email: '',
                        password: '',
                        confirmPassword: ''
                      })}
                      disabled={isSubmitting}
                    >
                      <i className="fas fa-undo me-2"></i>
                      Reset Form
                    </button>
                    
                    <button
                      type="submit"
                      className="btn btn-success btn-lg px-4"
                      disabled={isSubmitting || !formData.name.trim() || !formData.email.trim() || !formData.password}
                    >
                      {isSubmitting ? (
                        <>
                          <span className="spinner-border spinner-border-sm me-2"></span>
                          Creating...
                        </>
                      ) : (
                        <>
                          <i className="fas fa-user-plus me-2"></i>
                          Create Doctor Account
                        </>
                      )}
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>

          {/* Doctors Summary */}
          <div className="col-lg-4">
            <div className="card border-0 shadow-sm">
              <div className="card-header bg-light">
                <h6 className="mb-0">
                  <i className="fas fa-users me-2 text-success"></i>
                  Doctors Overview
                </h6>
              </div>
              <div className="card-body">
                <div className="text-center mb-3">
                  <div className="h2 text-success">{doctors.filter(d => d.is_active !== false).length}</div>
                  <div className="text-muted">Total Active Doctors</div>
                </div>

                <div className="border-top pt-3">
                  <h6 className="text-muted mb-3">Recent Doctors</h6>
                  {doctors
                    .filter(d => d.is_active !== false)  // Only show active doctors
                    .slice(-3)
                    .map(doctor => (
                      <div key={doctor.id} className="d-flex align-items-center mb-2">
                        <div className="bg-success rounded-circle p-2 me-3">
                          <i className="fas fa-user-md text-white"></i>
                        </div>
                        <div className="flex-grow-1">
                          <div className="fw-bold small">{doctor.name}</div>
                          <div className="text-muted small">{doctor.email}</div>
                        </div>
                      </div>
                    ))
                  }
                </div>

                <div className="border-top pt-3 mt-3">
                  <div className="d-grid">
                    <button 
                      className="btn btn-outline-success btn-sm"
                      onClick={() => {
                        // Scroll to the doctors list section
                        document.getElementById('doctors-list').scrollIntoView({ behavior: 'smooth' });
                      }}
                    >
                      <i className="fas fa-list me-2"></i>
                      View All Doctors
                    </button>
                  </div>
                </div>
              </div>
            </div>

            {/* Password Security Tips */}
            <div className="card border-0 shadow-sm mt-3">
              <div className="card-header bg-warning text-white">
                <h6 className="mb-0">
                  <i className="fas fa-shield-alt me-2"></i>
                  Security Tips
                </h6>
              </div>
              <div className="card-body">
                <div className="small">
                  <div className="mb-2">
                    <i className="fas fa-check text-success me-2"></i>
                    Use strong passwords (12+ characters)
                  </div>
                  <div className="mb-2">
                    <i className="fas fa-check text-success me-2"></i>
                    Include numbers and special characters
                  </div>
                  <div className="mb-2">
                    <i className="fas fa-check text-success me-2"></i>
                    Share credentials securely
                  </div>
                  <div className="mb-0">
                    <i className="fas fa-check text-success me-2"></i>
                    Advise doctors to change default password
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Existing Doctors List */}
        <div id="doctors-list" className="row mt-4">
          <div className="col-12">
            <div className="card border-0 shadow-sm">
              <div className="card-header bg-light d-flex justify-content-between align-items-center">
                <h6 className="mb-0">
                  <i className="fas fa-list me-2 text-success"></i>
                  {showInactiveDoctors ? 'All Doctors' : 'Active Doctors'} ({showInactiveDoctors 
                    ? doctors.length 
                    : doctors.filter(d => d.is_active !== false && !d.email.startsWith('deleted_')).length})
                </h6>
                <button 
                  className="btn btn-sm btn-outline-secondary" 
                  onClick={() => setShowInactiveDoctors(!showInactiveDoctors)}
                >
                  {showInactiveDoctors 
                    ? <><i className="fas fa-user-check me-1"></i> Show Active Only</>
                    : <><i className="fas fa-user-slash me-1"></i> Show Deleted</>
                  }
                </button>
              </div>
              <div className="card-body">
                {doctors.length > 0 ? (
                  <>
                    {/* Desktop Table */}
                    <div className="table-responsive d-none d-md-block">
                      <table className="table table-hover">
                        <thead>
                          <tr>
                            <th>Name</th>
                            <th>Email</th>
                            <th>Created Date</th>
                            <th>Status</th>
                            <th>Actions</th>
                          </tr>
                        </thead>
                        <tbody>
                          {(showInactiveDoctors 
                            ? doctors 
                            : doctors.filter(d => d.is_active !== false && !d.email.startsWith('deleted_'))
                          ).map(doctor => (
                            <tr key={doctor.id}>
                              <td>
                                <div className="d-flex align-items-center">
                                  <div className={`rounded-circle p-2 me-3 ${doctor.is_active === false || doctor.email.startsWith('deleted_') 
                                    ? 'bg-secondary' : 'bg-success'}`}>
                                    <i className="fas fa-user-md text-white small"></i>
                                  </div>
                                  <strong>{doctor.name}</strong>
                                </div>
                              </td>
                              <td>{doctor.email}</td>
                              <td>{new Date(doctor.created_at).toLocaleDateString()}</td>
                              <td>
                                {doctor.is_active === false || doctor.email.startsWith('deleted_')
                                  ? <span className="badge bg-secondary">Inactive</span>
                                  : <span className="badge bg-success">Active</span>
                                }
                              </td>
                              <td>
                                <button
                                  className="btn btn-outline-primary btn-sm me-2"
                                  onClick={() => handleEditClick(doctor)}
                                  disabled={doctor.is_active === false || doctor.email.startsWith('deleted_')}
                                  title="Edit doctor information"
                                >
                                  <i className="fas fa-edit"></i>
                                </button>
                                <button
                                  className="btn btn-outline-warning btn-sm me-2"
                                  onClick={() => handlePasswordClick(doctor)}
                                  disabled={doctor.is_active === false || doctor.email.startsWith('deleted_')}
                                  title="Change doctor password"
                                >
                                  <i className="fas fa-key"></i>
                                </button>
                                <button
                                  className="btn btn-outline-danger btn-sm"
                                  onClick={() => handleDeleteClick(doctor)}
                                  disabled={doctor.is_active === false || doctor.email.startsWith('deleted_')}
                                  title="Delete doctor"
                                >
                                  <i className="fas fa-trash"></i>
                                </button>
                                {/* Reactivate button for inactive doctors */}
                                {(doctor.is_active === false || doctor.email.startsWith('deleted_')) && (
                                  <button
                                    className="btn btn-outline-success btn-sm ms-2"
                                    onClick={() => handleReactivateClick(doctor)}
                                    title="Reactivate doctor"
                                  >
                                    <i className="fas fa-user-check"></i>
                                  </button>
                                )}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                    {/* Mobile Cards */}
                    <div className="d-block d-md-none">
                      {(showInactiveDoctors 
                        ? doctors 
                        : doctors.filter(d => d.is_active !== false && !d.email.startsWith('deleted_'))
                      ).map(doctor => (
                        <div key={doctor.id} className="card mb-3 border border-success">
                          <div className="card-body p-3">
                            <div className="d-flex align-items-center mb-2">
                              <div className={`rounded-circle p-2 me-3 ${doctor.is_active === false || doctor.email.startsWith('deleted_') 
                                ? 'bg-secondary' : 'bg-success'}`}>
                                <i className="fas fa-user-md text-white small"></i>
                              </div>
                              <div>
                                <div className="fw-bold">{doctor.name}</div>
                                <div className="small text-muted">{doctor.email}</div>
                              </div>
                            </div>
                            <div className="mb-2">
                              <strong>Status:</strong>{" "}
                              {doctor.is_active === false || doctor.email.startsWith('deleted_')
                                ? <span className="badge bg-secondary">Inactive</span>
                                : <span className="badge bg-success">Active</span>
                              }
                            </div>
                            <div className="mb-2">
                              <strong>Created:</strong>{" "}
                              {new Date(doctor.created_at).toLocaleDateString()}
                            </div>
                            <div>
                              <button
                                className="btn btn-outline-primary btn-sm me-2"
                                onClick={() => handleEditClick(doctor)}
                                disabled={doctor.is_active === false || doctor.email.startsWith('deleted_')}
                              >
                                <i className="fas fa-edit"></i> Edit
                              </button>
                              <button
                                className="btn btn-outline-warning btn-sm me-2"
                                onClick={() => handlePasswordClick(doctor)}
                                disabled={doctor.is_active === false || doctor.email.startsWith('deleted_')}
                              >
                                <i className="fas fa-key"></i> Change Password
                              </button>
                              <button
                                className="btn btn-outline-danger btn-sm"
                                onClick={() => handleDeleteClick(doctor)}
                                disabled={doctor.is_active === false || doctor.email.startsWith('deleted_')}
                              >
                                <i className="fas fa-trash"></i> Delete
                              </button>
                              {/* Reactivate button for inactive doctors */}
                              {(doctor.is_active === false || doctor.email.startsWith('deleted_')) && (
                                <button
                                  className="btn btn-outline-success btn-sm ms-2"
                                  onClick={() => handleReactivateClick(doctor)}
                                >
                                  <i className="fas fa-user-check"></i> Reactivate
                                </button>
                              )}
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </>
                ) : (
                  <div className="text-center py-4">
                    <i className="fas fa-user-md fa-3x text-muted mb-3"></i>
                    <h6 className="text-muted">No Doctors Created Yet</h6>
                    <p className="text-muted">Create your first doctor account using the form above.</p>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
        
        {/* Delete Confirmation Modal */}
        {showDeleteModal && (
          <div className="modal fade show" style={{ display: 'block', backgroundColor: 'rgba(0,0,0,0.5)' }} tabIndex="-1">
            <div className="modal-dialog modal-dialog-centered">
              <div className="modal-content" style={{ backgroundColor: 'white', color: 'black' }}>
                <div className="modal-header" style={{ backgroundColor: '#f8f9fa', borderBottom: '1px solid #dee2e6' }}>
                  <h5 className="modal-title" style={{ color: '#495057' }}>
                    <i className="fas fa-exclamation-triangle me-2" style={{ color: '#dc3545' }}></i>
                    Confirm Delete
                  </h5>
                  <button
                    type="button"
                    className="btn-close"
                    onClick={() => setShowDeleteModal(false)}
                    disabled={isDeleting}
                    style={{ color: '#6c757d' }}
                  ></button>
                </div>
                <div className="modal-body" style={{ backgroundColor: 'white', color: 'black' }}>
                  <p style={{ color: '#495057' }}>Are you sure you want to delete <strong>{deleteDoctor?.name}</strong>?</p>
                  <div className="alert alert-warning">
                    <i className="fas fa-exclamation-circle me-2"></i>
                    This will permanently remove the doctor account and any associated data. This action cannot be undone.
                  </div>
                  <div className="form-check mt-3">
                    <input
                      type="checkbox"
                      className="form-check-input"
                      id="deactivateOnly"
                      checked={deactivateOnly}
                      onChange={(e) => setDeactivateOnly(e.target.checked)}
                      disabled={isDeleting}
                    />
                    <label className="form-check-label" htmlFor="deactivateOnly" style={{ color: '#495057' }}>
                      <strong>Deactivate only</strong> - Set as inactive without changing email (prevents duplicate key errors)
                    </label>
                  </div>
                </div>
                <div className="modal-footer" style={{ backgroundColor: '#f8f9fa', borderTop: '1px solid #dee2e6' }}>
                  <button
                    type="button"
                    className="btn btn-outline-secondary"
                    onClick={() => setShowDeleteModal(false)}
                    disabled={isDeleting}
                    style={{ color: '#6c757d', borderColor: '#6c757d' }}
                  >
                    Close
                  </button>
                  <button
                    type="button"
                    className="btn btn-danger"
                    onClick={handleDeleteConfirm}
                    disabled={isDeleting}
                  >
                    {isDeleting ? (
                      <>
                        <span className="spinner-border spinner-border-sm me-2"></span>
                        Deleting...
                      </>
                    ) : (
                      <>
                        <i className={deactivateOnly ? "fas fa-user-slash me-2" : "fas fa-trash me-2"}></i>
                        {deactivateOnly ? "Deactivate" : "Delete Doctor"}
                      </>
                    )}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
        
        {/* Reactivate Confirmation Modal */}
        {showReactivateModal && (
          <div className="modal fade show" style={{ display: 'block', backgroundColor: 'rgba(0,0,0,0.5)' }} tabIndex="-1">
            <div className="modal-dialog modal-dialog-centered">
              <div className="modal-content" style={{ backgroundColor: 'white', color: 'black' }}>
                <div className="modal-header" style={{ backgroundColor: '#f8f9fa', borderBottom: '1px solid #dee2e6' }}>
                  <h5 className="modal-title" style={{ color: '#495057' }}>
                    <i className="fas fa-user-check me-2" style={{ color: '#28a745' }}></i>
                    Confirm Reactivate
                  </h5>
                  <button
                    type="button"
                    className="btn-close"
                    onClick={() => setShowReactivateModal(false)}
                    disabled={isReactivating}
                    style={{ color: '#6c757d' }}
                  ></button>
                </div>
                <div className="modal-body" style={{ backgroundColor: 'white', color: 'black' }}>
                  <p style={{ color: '#495057' }}>Are you sure you want to reactivate <strong>{reactivateDoctor?.name}</strong>?</p>
                  <div className="alert alert-info">
                    <i className="fas fa-info-circle me-2"></i>
                    This will restore the doctor's access to the system.
                  </div>
                </div>
                <div className="modal-footer" style={{ backgroundColor: '#f8f9fa', borderTop: '1px solid #dee2e6' }}>
                  <button
                    type="button"
                    className="btn btn-outline-secondary"
                    onClick={() => setShowReactivateModal(false)}
                    disabled={isReactivating}
                    style={{ color: '#6c757d', borderColor: '#6c757d' }}
                  >
                    Close
                  </button>
                  <button
                    type="button"
                    className="btn btn-success"
                    onClick={handleReactivateConfirm}
                    disabled={isReactivating}
                  >
                    {isReactivating ? (
                      <>
                        <span className="spinner-border spinner-border-sm me-2"></span>
                        Reactivating...
                      </>
                    ) : (
                      <>
                        <i className="fas fa-user-check me-2"></i>
                        Reactivate Doctor
                      </>
                    )}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
        
        {/* Edit Doctor Modal */}
        {showEditModal && (
          <div className="modal fade show" style={{ display: 'block', backgroundColor: 'rgba(0,0,0,0.5)' }} tabIndex="-1">
            <div className="modal-dialog modal-dialog-centered modal-lg">
              <div className="modal-content" style={{ backgroundColor: 'white', color: 'black' }}>
                <div className="modal-header" style={{ backgroundColor: '#f8f9fa', borderBottom: '1px solid #dee2e6' }}>
                  <h5 className="modal-title" style={{ color: '#495057' }}>
                    <i className="fas fa-edit me-2" style={{ color: '#007bff' }}></i>
                    Edit Doctor Account
                  </h5>
                  <button
                    type="button"
                    className="btn-close"
                    onClick={() => setShowEditModal(false)}
                    disabled={isUpdating}
                    style={{ color: '#6c757d' }}
                  ></button>
                </div>
                <form onSubmit={handleEditSubmit}>
                  <div className="modal-body" style={{ backgroundColor: 'white', color: 'black' }}>
                    {error && (
                      <div className="alert alert-danger">
                        <i className="fas fa-exclamation-triangle me-2"></i>
                        {error}
                      </div>
                    )}

                    <div className="row mb-3">
                      <div className="col-md-6">
                        <label htmlFor="editName" className="form-label" style={{ color: '#495057' }}>
                          <i className="fas fa-user me-2" style={{ color: '#007bff' }}></i>
                          Full Name *
                        </label>
                        <input
                          type="text"
                          className="form-control"
                          id="editName"
                          name="name"
                          value={editFormData.name}
                          onChange={handleEditInputChange}
                          required
                          style={{ color: 'black', backgroundColor: 'white', border: '1px solid #ced4da' }}
                        />
                      </div>
                      <div className="col-md-6">
                        <label htmlFor="editEmail" className="form-label" style={{ color: '#495057' }}>
                          <i className="fas fa-envelope me-2" style={{ color: '#007bff' }}></i>
                          Email Address *
                        </label>
                        <input
                          type="email"
                          className="form-control"
                          id="editEmail"
                          name="email"
                          value={editFormData.email}
                          onChange={handleEditInputChange}
                          required
                          style={{ color: 'black', backgroundColor: 'white', border: '1px solid #ced4da' }}
                        />
                      </div>
                    </div>

                    <div className="alert alert-info">
                      <i className="fas fa-info-circle me-2"></i>
                      Leave the password fields empty to keep the current password.
                    </div>

                    <div className="row">
                      <div className="col-md-6">
                        <label htmlFor="editPassword" className="form-label" style={{ color: '#495057' }}>
                          <i className="fas fa-lock me-2" style={{ color: '#007bff' }}></i>
                          New Password
                        </label>
                        <div className="input-group">
                          <input
                            type="password"
                            className="form-control"
                            id="editPassword"
                            name="password"
                            value={editFormData.password}
                            onChange={handleEditInputChange}
                            placeholder="Leave empty to keep current password"
                            style={{ color: 'black', backgroundColor: 'white', border: '1px solid #ced4da' }}
                          />
                          <button
                            type="button"
                            className="btn btn-outline-primary"
                            onClick={generateEditPassword}
                          >
                            <i className="fas fa-random"></i>
                          </button>
                        </div>
                        <div className="form-text" style={{ color: '#6c757d' }}>
                          Password must be at least 6 characters long.
                        </div>
                      </div>
                      <div className="col-md-6">
                        <label htmlFor="editConfirmPassword" className="form-label" style={{ color: '#495057' }}>
                          <i className="fas fa-check me-2" style={{ color: '#007bff' }}></i>
                          Confirm New Password
                        </label>
                        <input
                          type="password"
                          className="form-control"
                          id="editConfirmPassword"
                          name="confirmPassword"
                          value={editFormData.confirmPassword}
                          onChange={handleEditInputChange}
                          placeholder="Leave empty to keep current password"
                          style={{ color: 'black', backgroundColor: 'white', border: '1px solid #ced4da' }}
                        />
                      </div>
                    </div>
                  </div>
                  <div className="modal-footer" style={{ backgroundColor: '#f8f9fa', borderTop: '1px solid #dee2e6' }}>
                    <button
                      type="button"
                      className="btn btn-outline-secondary"
                      onClick={() => setShowEditModal(false)}
                      disabled={isUpdating}
                      style={{ color: '#6c757d', borderColor: '#6c757d' }}
                    >
                      Close
                    </button>
                    <button
                      type="submit"
                      className="btn btn-primary"
                      disabled={isUpdating}
                    >
                      {isUpdating ? (
                        <>
                          <span className="spinner-border spinner-border-sm me-2"></span>
                          Updating...
                        </>
                      ) : (
                        <>
                          <i className="fas fa-save me-2"></i>
                          Save Changes
                        </>
                      )}
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        )}

        {/* Password Change Modal */}
        {showPasswordModal && (
          <div className="modal fade show" style={{ display: 'block', backgroundColor: 'rgba(0,0,0,0.5)' }} tabIndex="-1">
            <div className="modal-dialog modal-dialog-centered">
              <div className="modal-content" style={{ backgroundColor: 'white', color: 'black' }}>
                <div className="modal-header" style={{ backgroundColor: '#f8f9fa', borderBottom: '1px solid #dee2e6' }}>
                  <h5 className="modal-title" style={{ color: '#495057' }}>
                    <i className="fas fa-key me-2" style={{ color: '#ffc107' }}></i>
                    Change Password for Dr. {passwordDoctor?.name}
                  </h5>
                  <button
                    type="button"
                    className="btn-close"
                    onClick={() => setShowPasswordModal(false)}
                    disabled={isChangingPassword}
                    style={{ color: '#6c757d' }}
                  ></button>
                </div>
                <form onSubmit={handlePasswordSubmit}>
                  <div className="modal-body" style={{ backgroundColor: 'white', color: 'black' }}>
                    {error && (
                      <div className="alert alert-danger">
                        <i className="fas fa-exclamation-triangle me-2"></i>
                        {error}
                      </div>
                    )}

                    <div className="alert alert-info">
                      <i className="fas fa-info-circle me-2"></i>
                      You are about to change the password for <strong>{passwordDoctor?.name}</strong> ({passwordDoctor?.email}).
                    </div>

                    <div className="mb-3">
                      <label htmlFor="newPassword" className="form-label" style={{ color: '#495057' }}>
                        <i className="fas fa-lock me-2" style={{ color: '#ffc107' }}></i>
                        New Password *
                      </label>
                      <div className="input-group">
                        <input
                          type="password"
                          className="form-control"
                          id="newPassword"
                          name="newPassword"
                          value={passwordFormData.newPassword}
                          onChange={handlePasswordInputChange}
                          placeholder="Enter new password"
                          required
                          style={{ color: 'black', backgroundColor: 'white', border: '1px solid #ced4da' }}
                        />
                        <button
                          type="button"
                          className="btn btn-outline-warning"
                          onClick={generatePasswordChangePassword}
                        >
                          <i className="fas fa-random"></i>
                        </button>
                      </div>
                      <div className="form-text" style={{ color: '#6c757d' }}>
                        Password must be at least 6 characters long.
                      </div>
                    </div>

                    <div className="mb-3">
                      <label htmlFor="confirmNewPassword" className="form-label" style={{ color: '#495057' }}>
                        <i className="fas fa-check me-2" style={{ color: '#ffc107' }}></i>
                        Confirm New Password *
                      </label>
                      <input
                        type="password"
                        className="form-control"
                        id="confirmNewPassword"
                        name="confirmNewPassword"
                        value={passwordFormData.confirmNewPassword}
                        onChange={handlePasswordInputChange}
                        placeholder="Confirm new password"
                        required
                        style={{ color: 'black', backgroundColor: 'white', border: '1px solid #ced4da' }}
                      />
                    </div>

                    <div className="alert alert-warning">
                      <i className="fas fa-exclamation-triangle me-2"></i>
                      <strong>Important:</strong> Make sure to communicate the new password to the doctor securely.
                    </div>
                  </div>
                  <div className="modal-footer" style={{ backgroundColor: '#f8f9fa', borderTop: '1px solid #dee2e6' }}>
                    <button
                      type="button"
                      className="btn btn-outline-secondary"
                      onClick={() => setShowPasswordModal(false)}
                      disabled={isChangingPassword}
                      style={{ color: '#6c757d', borderColor: '#6c757d' }}
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      className="btn btn-warning"
                      disabled={isChangingPassword || !passwordFormData.newPassword || !passwordFormData.confirmNewPassword}
                    >
                      {isChangingPassword ? (
                        <>
                          <span className="spinner-border spinner-border-sm me-2"></span>
                          Changing Password...
                        </>
                      ) : (
                        <>
                          <i className="fas fa-key me-2"></i>
                          Change Password
                        </>
                      )}
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        )}

      </div>
    </ResearchLayout>
  );
};

export default ProgramAdminAddDoctors;